import {useState} from 'react';

import {SegmentedControl} from 'shells/card/segmented-control';
import {FormField} from 'shells/form/form-field';
import {Stack} from 'shells/layout/stack/stack';
import type {SavedSettingToastState} from 'shells/saved-setting-toast/saved-setting-toast';

import type {LinkNumberTypeEnum} from 'nutshell-graphql-types';
import type {ChatSettingsActionsInput} from 'nutshell-graphql-types';

import {CallTrackerSelect} from './calltracker-select';
import {SAVE_STATE_KEYS, CALL_TRACKER_OPTIONS} from './consts';
import type {SaveStateKey} from './consts';
import {CustomNumberInput} from './custom-number-input';
import {useDebouncedSaveField} from './use-debounced-save-field';

type Props = {
    linkedNumberType: LinkNumberTypeEnum;
    savingToastKey: SaveStateKey | null | undefined;
    toastState: SavedSettingToastState;
    customNumber: string | null | undefined;
    callTrackerNumber: string | null | undefined;
    callTrackerId: string | null | undefined;
    handleSave: (updates: ChatSettingsActionsInput, saveStateKey: SaveStateKey) => Promise<unknown>;
};

export const PhoneNumberInput = (props: Props) => {
    const [selectedValue, setSelectedValue] = useState<LinkNumberTypeEnum>(props.linkedNumberType);

    const shouldShowCallTrackerSelectOptions = NutClientConfig.isWebfx;
    const shouldShowCallTrackerSelector =
        NutClientConfig.isWebfx && selectedValue === 'callTracker';

    const savedCustomNumber = props.customNumber || '';
    const savedCallTrackerNumber = props.callTrackerNumber || '';
    const savedLinkNumberType = props.linkedNumberType;
    const savedCallTrackerId = props.callTrackerId || '';

    const customNumber = useDebouncedSaveField({
        savedValue: savedCustomNumber,
        handleSave: (newNumber) =>
            props.handleSave(
                {call: {linkNumberConfig: {customNumber: newNumber}}},
                SAVE_STATE_KEYS.CALL_LINKED_PHONE_NUMBER
            ),
        validation: {isRequired: true},
    });

    const callTrackerNumber = useDebouncedSaveField({
        savedValue: savedCallTrackerNumber,
        handleSave: (newNumber) =>
            props.handleSave(
                {call: {linkNumberConfig: {callTrackerNumber: newNumber}}},
                SAVE_STATE_KEYS.CALL_LINKED_PHONE_NUMBER
            ),
    });

    const linkedNumberType = useDebouncedSaveField({
        savedValue: savedLinkNumberType,
        handleSave: (newType) =>
            props.handleSave(
                {call: {linkNumberConfig: {type: newType}}},
                SAVE_STATE_KEYS.CALL_LINKED_PHONE_NUMBER
            ),
    });

    const callTrackerId = useDebouncedSaveField({
        savedValue: savedCallTrackerId,
        handleSave: (newId) =>
            props.handleSave(
                {call: {linkNumberConfig: {callTrackerId: newId}}},
                SAVE_STATE_KEYS.CALL_LINKED_PHONE_NUMBER
            ),
    });

    return (
        <FormField
            innerId='phone-number'
            label='Phone number'
            saveToastState={
                props.savingToastKey === SAVE_STATE_KEYS.CALL_LINKED_PHONE_NUMBER
                    ? props.toastState
                    : 'initial'
            }
        >
            <Stack spacing={8}>
                {shouldShowCallTrackerSelectOptions && (
                    <SegmentedControl
                        buttonOptions={CALL_TRACKER_OPTIONS}
                        selectedValue={selectedValue}
                        setSelectedValue={(value: LinkNumberTypeEnum) => setSelectedValue(value)}
                        onChange={linkedNumberType.onChange}
                    />
                )}
                {shouldShowCallTrackerSelector ? (
                    <CallTrackerSelect
                        id={callTrackerId.value}
                        onChangeId={callTrackerId.onChange}
                        onChangeNumber={callTrackerNumber.onChange}
                    />
                ) : (
                    <CustomNumberInput
                        value={customNumber.value}
                        onChangeCustomNumber={customNumber.onChange}
                        onBlur={customNumber.onBlur}
                        errorMessage={customNumber.errorMessage}
                    />
                )}
            </Stack>
        </FormField>
    );
};
